// Global Budget Configuration Storage
// Manages the storage of org-level budget mode configuration.
// Uses the API as the source of truth with localStorage as a sync cache
// for faster initial renders.

import type {
  BudgetConfiguration,
  BudgetMode,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  BudgetTrackingType,
} from '@/types/budget-configuration.types';
import { DEFAULT_BUDGET_CONFIGURATION } from '@/types/budget-configuration.types';
import {
  fetchBudgetConfiguration as apiFetchBudgetConfiguration,
  updateBudgetConfiguration as apiUpdateBudgetConfiguration,
} from '@/lib/data-service';

const STORAGE_KEY = 'budget-configuration-v3';

// ── Migration helper ────────────────────────────────────────────────────────

/**
 * Migrate a value that may have been stored with the old `BudgetTrackingType`
 * shape to the new `BudgetMode` shape.
 *
 * Old shape:  { trackingType: 'simple' | 'monthly' | 'none' }
 * New shape:  { budgetMode: 'category' | 'cost-centre' | 'disabled', ... }
 */
function migrateStoredValue(raw: Record<string, unknown>): BudgetConfiguration {
  // Already in new shape
  if (typeof raw.budgetMode === 'string') {
    return {
      ...DEFAULT_BUDGET_CONFIGURATION,
      ...(raw as Partial<BudgetConfiguration>),
    };
  }

  // Migrate from old shape
  const oldTrackingType = raw.trackingType as string | undefined;
  let budgetMode: BudgetMode = 'category';
  if (oldTrackingType === 'none') {
    budgetMode = 'disabled';
  } else if (oldTrackingType === 'simple' || oldTrackingType === 'monthly') {
    budgetMode = 'category';
  }

  return {
    ...DEFAULT_BUDGET_CONFIGURATION,
    budgetMode,
    lastUpdated: raw.lastUpdated as string | undefined,
    updatedBy: raw.updatedBy as string | undefined,
  };
}

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * Get the current budget configuration from localStorage cache (synchronous).
 * Use this for initial render; follow up with `fetchBudgetConfigurationAsync`
 * to get the server-authoritative value.
 */
export function getBudgetConfiguration(): BudgetConfiguration {
  try {
    // Check new key first
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Record<string, unknown>;
      return migrateStoredValue(parsed);
    }

    // Attempt to read from old key and migrate
    const oldStored = localStorage.getItem('budget-configuration');
    if (oldStored) {
      const parsed = JSON.parse(oldStored) as Record<string, unknown>;
      const migrated = migrateStoredValue(parsed);
      // Persist the migrated value to the new key
      localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
      return migrated;
    }
  } catch (error) {
    console.error('Error loading budget configuration:', error);
  }

  return { ...DEFAULT_BUDGET_CONFIGURATION };
}

/**
 * Fetch budget configuration from the API and update localStorage cache.
 * Returns the server-authoritative configuration.
 */
export async function fetchBudgetConfigurationAsync(): Promise<BudgetConfiguration> {
  try {
    const data = await apiFetchBudgetConfiguration();
    const config: BudgetConfiguration = {
      budgetMode: data.budgetMode as BudgetMode,
      costBudgetEnabled: data.costBudgetEnabled,
      salesBudgetEnabled: data.salesBudgetEnabled,
      resourceBudgetEnabled: data.resourceBudgetEnabled,
      hasBudgetData: data.hasBudgetData ?? false,
      lastUpdated: new Date().toISOString(),
    };
    // Update localStorage cache
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(config)); } catch { /* ignore */ }
    return config;
  } catch (error) {
    console.error('Error fetching budget configuration from API:', error);
    // Fall back to localStorage cache
    return getBudgetConfiguration();
  }
}

/**
 * Save budget configuration to both the API and localStorage cache.
 */
export async function saveBudgetConfigurationAsync(config: BudgetConfiguration): Promise<void> {
  const configWithMetadata: BudgetConfiguration = {
    ...config,
    lastUpdated: new Date().toISOString(),
  };
  // Write-through to localStorage for immediate UI consistency
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(configWithMetadata)); } catch { /* ignore */ }
  // Persist to the API
  await apiUpdateBudgetConfiguration({
    budgetMode: configWithMetadata.budgetMode,
    costBudgetEnabled: configWithMetadata.costBudgetEnabled,
    salesBudgetEnabled: configWithMetadata.salesBudgetEnabled,
    resourceBudgetEnabled: configWithMetadata.resourceBudgetEnabled,
  });
}

/**
 * Save budget configuration to localStorage (sync fallback).
 * Also fires the async API call but does not await it in callers that
 * cannot be async.
 */
export function saveBudgetConfiguration(config: BudgetConfiguration): void {
  const configWithMetadata: BudgetConfiguration = {
    ...config,
    lastUpdated: new Date().toISOString(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(configWithMetadata));
  } catch (error) {
    console.error('Error saving budget configuration:', error);
    throw error;
  }
  // Fire-and-forget API persist
  apiUpdateBudgetConfiguration({
    budgetMode: configWithMetadata.budgetMode,
    costBudgetEnabled: configWithMetadata.costBudgetEnabled,
    salesBudgetEnabled: configWithMetadata.salesBudgetEnabled,
    resourceBudgetEnabled: configWithMetadata.resourceBudgetEnabled,
  }).catch((err) => {
    console.error('Error persisting budget configuration to API:', err);
  });
}

/**
 * Get just the budget mode (convenience getter).
 */
export function getBudgetMode(): BudgetMode {
  return getBudgetConfiguration().budgetMode;
}

/**
 * Update just the budget mode (convenience setter).
 */
export function updateBudgetMode(budgetMode: BudgetMode): void {
  const config = getBudgetConfiguration();
  saveBudgetConfiguration({ ...config, budgetMode });
}

/**
 * Update the category-level toggles (only meaningful in 'category' mode).
 */
export function updateBudgetCategoryToggles(
  toggles: Partial<Pick<BudgetConfiguration, 'costBudgetEnabled' | 'salesBudgetEnabled' | 'resourceBudgetEnabled'>>
): void {
  const config = getBudgetConfiguration();
  saveBudgetConfiguration({ ...config, ...toggles });
}

/**
 * Clear budget configuration (reset to default).
 */
export function clearBudgetConfiguration(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem('budget-configuration'); // also clear old key
  } catch (error) {
    console.error('Error clearing budget configuration:', error);
  }
}

// ── Legacy compatibility helpers (kept so old callers compile) ──────────────
// These will be removed when tasks 1.6–1.8 (settings UI redesign) are done.

/** @deprecated Use `getBudgetMode()` instead. */
export function getBudgetTrackingType(): 'simple' | 'monthly' | 'none' {
  const mode = getBudgetMode();
  if (mode === 'disabled') return 'none';
  // Both 'category' and 'cost-centre' map to 'monthly' (monthly is locked in P1)
  return 'monthly';
}

/** @deprecated Use `updateBudgetMode()` instead. */
export function updateBudgetTrackingType(trackingType: 'simple' | 'monthly' | 'none'): void {
  let budgetMode: BudgetMode = 'category';
  if (trackingType === 'none') budgetMode = 'disabled';
  updateBudgetMode(budgetMode);
}
